import { useContext, useMemo, useState } from "react";

import { CursorChangeProps } from "../../../../../../../models";
import { getProjectIssuances, GetProjectIssuancesRequest } from "../../../../../../../service/publicquery";
import { dataGridMapFilterCriteria } from "../../../../../../../widget";
import { ProjectContext } from "../../../../../../shared/projects/project/ProjectContext";
import { PRE_ACCESS_PAGE_SIZE } from "./constants/preAccessInventoryTab.constants";
import {
  PreAccessInventoryOnChange,
  PreAccessInventoryTabProps,
  UsePreAccessInventoryTabReturnData,
} from "./models/preAccessInventoryTab.models";
import {
  buildEmptyResultType,
  buildPreAccessChartData,
  getPreAccessColumns,
  getPreAccessDefaultSortingCriteria,
  getPreAccessDefaultSortKey,
  mapPreAccessIssuancesToRows,
} from "./utils/preAccessInventoryTab.utils";

export const usePreAccessInventoryTab = ({
  projectUuid,
  unitMeasurement,
}: PreAccessInventoryTabProps): UsePreAccessInventoryTabReturnData => {
  const { publicProjectDetails } = useContext(ProjectContext);
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughIssuancesForScrolling, setHasEnoughIssuancesForScrolling] = useState(false);
  const [stackedChartData, setStackedChartData] = useState<UsePreAccessInventoryTabReturnData["stackedChartData"]>();
  const [xAxisLabels, setXAxisLabels] = useState<UsePreAccessInventoryTabReturnData["xAxisLabels"]>([]);

  const showAvailable = useMemo(() => {
    return !!publicProjectDetails && publicProjectDetails.listing.availableForSale;
  }, [publicProjectDetails]);

  const onChange: PreAccessInventoryOnChange = async ({ filtering, paging, sorting }: CursorChangeProps) => {
    const pageSize = paging.pageSize ?? PRE_ACCESS_PAGE_SIZE;
    let data = buildEmptyResultType(pageSize);

    if (!projectUuid) {
      setIsLoading(false);
      return data;
    }

    setIsLoading(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);
    const sortKeyMap: Record<string, NonNullable<GetProjectIssuancesRequest["sort"]>[number]["key"]> = {
      vintage: "results.vintageStartDate",
      issuanceDate: "results.issuanceDate",
      habitatType: "results.habitatType",
      habitat: "results.habitat",
      distinctiveness: "results.distinctiveness",
      unitQuantityTotal: "results.unitQuantityTotal",
      unitQuantityAvailable: "results.unitQuantityAvailable",
      quantityType: "results.quantityType",
      price: "results.price",
    };

    await getProjectIssuances({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      sort:
        sorting.length > 0
          ? sorting.map((sort) => ({
              key: sortKeyMap[sort.key] || getPreAccessDefaultSortKey(unitMeasurement),
              direction: sort.direction,
            }))
          : [
              {
                key: getPreAccessDefaultSortKey(unitMeasurement),
                direction: "asc",
              },
            ],
      filter: { results: filterCriteria },
      projectUuid,
    })
      .then((response) => {
        const issuances = response.data?.results ?? [];
        data = {
          resultData: mapPreAccessIssuancesToRows(issuances, unitMeasurement),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };

        const chartData = buildPreAccessChartData(issuances, unitMeasurement, showAvailable);

        setHasData(issuances.length > 0);
        setHasEnoughIssuancesForScrolling(issuances.length > 5);
        setXAxisLabels(chartData.xAxisLabels);
        setStackedChartData(chartData.stackedChartData);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  return {
    columns: getPreAccessColumns(
      unitMeasurement,
      !!publicProjectDetails && publicProjectDetails.listing.showPriceInDirectory,
      showAvailable
    ),
    defaultSortingCriteria: getPreAccessDefaultSortingCriteria(unitMeasurement),
    hasData,
    hasEnoughIssuancesForScrolling,
    isLoading,
    onChange,
    stackedChartData,
    xAxisLabels,
    showAvailable,
  };
};
